import React, { useState } from "react";
import { Send, CheckCircle, Phone, Mail, Loader2 } from "lucide-react";
import { gtmPush } from "../utils/gtm";

function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // const [showForm, setShowForm] = useState(true);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    gtmPush("form_field_interaction", {
      form_name: "contact_form",
      field_name: name,
      field_value_length: value.length,
      event_category: "form",
      event_label: name,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    gtmPush("lead_form_start", {
      form_name: "contact_form",
      form_location: "homepage_contact_section",
      form_fields: Object.keys(formData),
    });

    console.log(formData);
    setIsSubmitting(true);
    // setShowForm(false);

    // setIsSubmitted(true);

    const formData1 = new URLSearchParams();
    formData1.append("name", formData.name);
    formData1.append("email", formData.email);
    formData1.append("phone", formData.phone);
    formData1.append("message", formData.message);

    fetch(
      "https://script.google.com/macros/s/AKfycbyt9TBIzsPSpyFr0dWMqqMV36s8gX6RjlJ9NYk9UXq2uamtRmsvt9bM351_tBCuESIfBQ/exec",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formData1,
      }
    )
      .then((response) => response.json())
      .then((data) => {
        setIsSubmitted(true);
        gtmPush("lead_form_submit", {
          form_name: "contact_form",
          form_status: "success",
          lead_type: "general_contact",
          lead_value: 1,
        });
        console.log("Success:", data);
      })
      .catch((error) => {
        setIsSubmitted(true);
        gtmPush("lead_form_submit", {
          form_name: "contact_form",
          form_status: "error",
          lead_type: "general_contact",
          lead_value: 1,
        });
        console.error("Fetch error:", error);
      });
  };

  if (isSubmitted) {
    return (
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center bg-green-50 rounded-2xl p-12">
            <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Thank You!
            </h2>
            <p className="text-xl text-gray-600 mb-6">
              Your message has been sent successfully. I'll get back to you
              within 24 hours.
            </p>
            <div className="bg-white rounded-lg p-6 shadow-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                What's Next?
              </h3>
              <ul className="text-left space-y-2 text-gray-600">
                <li>• I'll review your message and goals</li>
                <li>• Schedule a complimentary 15-minute consultation</li>
                <li>• Discuss your coaching needs and options</li>
                <li>• Create a personalized action plan</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Get In Touch
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Ready to start your transformation journey? Let's connect and
            discuss how I can help you achieve your goals.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="bg-purple-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Let's Start Your Journey
              </h3>
              <p className="text-gray-600 mb-8">
                Fill out the form and I'll personally reach out to discuss your
                goals and how we can work together to achieve them.
              </p>

              <div className="space-y-6">
                <div className="flex items-center">
                  <Phone className="h-6 w-6 text-purple-600 mr-4" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Call Me</h4>
                    <p className="text-gray-600">+91 8977895528</p>
                  </div>
                </div>

                <div className="flex items-center">
                  <Mail className="h-6 w-6 text-purple-600 mr-4" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Email Me</h4>
                    <p className="text-gray-600">
                      info.srilatharakesh@gmail.com
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="9290000000"
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Tell me about your goals and how I can help you..."
                />
              </div>

              <button
                type="submit"
                className="w-full bg-purple-600 text-white py-4 px-6 rounded-lg font-semibold hover:bg-purple-700 transition-colors flex items-center justify-center"
              >
                Send Message
                <Send className="ml-2 h-5 w-5" />
              </button>
            </form>
          </div>

          {isSubmitting && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-8 max-w-sm w-full mx-4 text-center">
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
                <p className="text-gray-700 font-medium">
                  Submitting your data...
                </p>
                <p className="text-gray-500 text-sm mt-2">Please wait...</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default ContactForm;
